import React from 'react';
import {
  Currency,
  ErrorHandler,
  Gutter,
  LoadingState,
  Paragraph,
  Text,
  Title,
} from '@common/components';
import { GET_STAFFING_REQUEST_TEMPLATE_QUERY } from 'works/legacy/graphql/staffing-request-template';
import { Col, Divider, Row } from 'antd';
import { useQuery } from '@common/hooks';
import { ENUM_QUERY } from '@common/graphql';
import { roleLevelParser } from '@institutions/services';

export function StaffingRequestTemplateDetailsContent({
  templateId,
}: {
  templateId: string;
}) {
  const {
    data: yearsOfExperienceData,
    loading: yearsOfExperienceLoading,
    error: yearsOfExperienceError,
  } = useQuery(ENUM_QUERY, {
    variables: { key: 'YearsOfExperience' },
  });

  const {
    loading: staffingRequestTypeLoading,
    error: staffingRequestTypeError,
    data: staffingRequestTypesData,
  } = useQuery(ENUM_QUERY, {
    variables: { key: 'StaffingRequestTypes' },
  });

  const {
    data,
    loading,
    error: staffingRequestTemplateError,
  } = useQuery(GET_STAFFING_REQUEST_TEMPLATE_QUERY, {
    variables: { id: templateId },
  });

  if (loading || yearsOfExperienceLoading || staffingRequestTypeLoading)
    return <LoadingState />;

  const error =
    staffingRequestTemplateError ||
    yearsOfExperienceError ||
    staffingRequestTypeError;

  if (error) {
    return (
      <ErrorHandler error={error} log="StaffingRequestTemplateDetailsContent" />
    );
  }

  const template = data?.staffingRequestTemplate;
  const formattedData = {
    name: <b>{template.name}</b>,
    facility: template.organization.name,
    location: template.organizationUnit.department.organizationBusiness.name,
    unit: template.organizationUnit.name,
    role: template.role.abbreviation,
    roleLevel: roleLevelParser(template.roleLevel),
    costCenter: template.organizationUnit.costCenter,
    regularRate: <Currency value={template.billingRegularRate} />,
    contractType: staffingRequestTypesData?.enum.find(
      ({ name }) => name === template.staffingRequestType
    )?.description,
    yearsOfExperience: yearsOfExperienceData?.enum.find(
      ({ name }) => name === template.yearsOfExperience
    )?.description,
    specialties:
      template.staffingRequestTemplateSpecialties.length > 0 &&
      template.staffingRequestTemplateSpecialties.map(
        (specialty: {
          clinicalSpecialty: { id: string; displayName: string };
          necessityLevel: string;
        }) => (
          <React.Fragment key={specialty.clinicalSpecialty.id}>
            {specialty.clinicalSpecialty.displayName}
            {specialty.necessityLevel === 'required' && <RequiredText />}
            <br />
          </React.Fragment>
        )
      ),
    technologies:
      template.staffingRequestTemplateTechnologies.length > 0 &&
      template.staffingRequestTemplateTechnologies.map(
        (technology: { technology: { id: string; name: string } }) => (
          <React.Fragment key={technology.technology.id}>
            {technology.technology.name}
            <br />
          </React.Fragment>
        )
      ),
    certifications:
      template.staffingRequestTemplateCertifications.length > 0 &&
      template.staffingRequestTemplateCertifications.map(
        (certification: {
          certification: { id: string; credential: string };
          necessityLevel: string;
        }) => (
          <React.Fragment key={certification.certification.id}>
            {certification.certification.credential}
            {certification.necessityLevel === 'required' && <RequiredText />}
            <br />
          </React.Fragment>
        )
      ),
    description: template.description,
  };

  const sections = [
    {
      name: 'Position Details',
      rows: [
        { label: 'Name', dataKey: 'name' },
        { label: 'Facility', dataKey: 'facility' },
        { label: 'Location', dataKey: 'location' },
        { label: 'Unit', dataKey: 'unit' },
        { label: 'Role', dataKey: 'role' },
        { label: 'Level', dataKey: 'roleLevel' },
        { label: 'Cost Center', dataKey: 'costCenter' },
        { label: 'Contract Type', dataKey: 'contractType' },
      ],
    },
    {
      name: 'Experience',
      rows: [
        { label: 'Years of Experience', dataKey: 'yearsOfExperience' },
        { label: 'Specialties', dataKey: 'specialties' },
        { label: 'Technologies', dataKey: 'technologies' },
      ],
    },
    {
      name: 'Certifications',
      rows: [{ label: 'Certifications', dataKey: 'certifications' }],
    },
  ];

  return (
    <div>
      {sections.map((section, sectionIdx) => {
        return (
          <div key={section.name}>
            {sectionIdx > 0 && <Divider />}
            <Gutter direction="bottom" size="md">
              <Title size="h3">{section.name}</Title>
            </Gutter>

            {section.rows.map(({ label, dataKey }) => {
              return (
                <Row key={dataKey} gutter={24}>
                  <Col span={8}>
                    <Text color="midnightLighter">{label}</Text>
                  </Col>
                  <Col span={16}>
                    <span>
                      {formattedData[dataKey as keyof typeof formattedData] ||
                        'N/A'}
                    </span>
                  </Col>
                </Row>
              );
            })}
          </div>
        );
      })}

      <div>
        <Divider />
        <Gutter direction="bottom" size="md">
          <Title size="h3">Description</Title>
        </Gutter>
        <Paragraph>{formattedData.description}</Paragraph>
      </div>
    </div>
  );
}

function RequiredText() {
  return (
    <Text color="steelDarkest" italic>
      {' '}
      (required)
    </Text>
  );
}
